'use client';

import type { JSX } from 'react';

import clsx from 'clsx';
import { ToastContainer, type ToastContainerProps } from 'react-toastify';

import { variantClasses } from '@/components/classes/variantClasses';
import { composeRadiusClasses } from '@/components/functions/composeRadiusClasses';
import type { Adaptive } from '@/components/types/Adaptive';
import type { IconType } from '@/components/types/IconType';
import type { Radius } from '@/components/types/Radius';
import type { Variant } from '@/components/types/Variant';
import { componentsConfig } from '@/config/componentsConfig';

import Button from '../Button';
import Icon from '../Icon';

import { showCustomToast, showErrorToast, showInfoToast, showSuccessToast, showWarningToast } from './ToastContent';

type ToastProps = {
    locale: string;
    variant?: Variant;
    radius?: Adaptive<Radius>;
    toastClassName?: ToastContainerProps['toastClassName'];
    hideProgressBar?: ToastContainerProps['hideProgressBar'];
    closeOnClick?: ToastContainerProps['closeOnClick'];
    pauseOnFocusLoss?: ToastContainerProps['pauseOnFocusLoss'];
    draggable?: ToastContainerProps['draggable'];
    pauseOnHover?: ToastContainerProps['pauseOnHover'];
    newestOnTop?: ToastContainerProps['newestOnTop'];
    autoClose?: ToastContainerProps['autoClose'];
    className?: ToastContainerProps['className'];
    closeIcon?: IconType;
    successIcon?: IconType;
    errorIcon?: IconType;
    warningIcon?: IconType;
    infoIcon?: IconType;
};

export default function Toast({
    autoClose = componentsConfig.toast.autoClose,
    locale,
    variant = componentsConfig.toast.variant,
    radius = componentsConfig.toast.radius,
    className,
    toastClassName,
    hideProgressBar = componentsConfig.toast.hideProgressBar,
    closeOnClick = componentsConfig.toast.closeOnClick,
    pauseOnFocusLoss = componentsConfig.toast.pauseOnFocusLoss,
    draggable = componentsConfig.toast.draggable,
    pauseOnHover = componentsConfig.toast.pauseOnHover,
    newestOnTop = componentsConfig.toast.newestOnTop,
    closeIcon = componentsConfig.toast.closeIcon,
    successIcon = componentsConfig.toast.successIcon,
    errorIcon = componentsConfig.toast.errorIcon,
    warningIcon = componentsConfig.toast.warningIcon,
    infoIcon = componentsConfig.toast.infoIcon,
}: ToastProps): JSX.Element {
    // ----------------------------------------------------------------------------------------------------
    // MARK: States & Constants
    // ----------------------------------------------------------------------------------------------------
    const position = locale === 'ar' ? 'top-left' : 'top-right';

    const rtl = locale === 'ar';

    // ----------------------------------------------------------------------------------------------------
    // MARK: Main Component UI
    // ----------------------------------------------------------------------------------------------------
    return (
        <ToastContainer
            position={position}
            autoClose={autoClose}
            rtl={rtl}
            className={className}
            hideProgressBar={hideProgressBar}
            closeOnClick={closeOnClick}
            pauseOnFocusLoss={pauseOnFocusLoss}
            draggable={draggable}
            pauseOnHover={pauseOnHover}
            newestOnTop={newestOnTop}
            closeButton={({ closeToast }) => (
                <Button variant="link" color="tertiary" justify="end" onClick={closeToast}>
                    <Icon icon={closeIcon} size={10} color="white" />
                </Button>
            )}
            icon={({ type }) => {
                switch (type) {
                    case 'info':
                        return <Icon icon={infoIcon} size={10} color="white" />;
                    case 'error':
                        return <Icon icon={errorIcon} size={10} color="white" />;
                    case 'success':
                        return <Icon icon={successIcon} size={10} color="white" />;
                    case 'warning':
                        return <Icon icon={warningIcon} size={10} color="white" />;
                    default:
                        return null;
                }
            }}
            toastClassName={(context) => {
                const type = context?.type;
                const defaultClassName = context?.defaultClassName;

                switch (type) {
                    case 'info':
                        return clsx(composeRadiusClasses(radius), toastClassName, defaultClassName, variantClasses[variant]['info']);
                    case 'error':
                        return clsx(composeRadiusClasses(radius), toastClassName, defaultClassName, variantClasses[variant]['danger']);
                    case 'success':
                        return clsx(composeRadiusClasses(radius), toastClassName, defaultClassName, variantClasses[variant]['accent']);
                    case 'warning':
                        return clsx(composeRadiusClasses(radius), toastClassName, defaultClassName, variantClasses[variant]['warning']);
                    default:
                        return '';
                }
            }}
        />
    );
}

// // Add static methods to Toast
Toast.success = showSuccessToast;
Toast.error = showErrorToast;
Toast.info = showInfoToast;
Toast.warning = showWarningToast;
Toast.custom = showCustomToast;
